import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GoogleAdsCallout } from '@feathr/blackbox';
import { CampaignClass, MAX_CALLOUT_TEXT } from '@feathr/blackbox';
import {
  Alert,
  Button,
  CardV2 as Card,
  ConfirmModal,
  Counter,
  Heading,
  Input,
  Spinner,
  Text,
  toast,
  Well,
} from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import {
  createDraftCallout,
  createEmptyDraftCallout,
  diffDraftCallouts,
  getDraftCalloutError,
  type IDraftCallout,
  isDraftDirty,
} from './GoogleAdsCallouts.utils';

import * as styles from './GoogleAdsCallouts.css';

interface IGoogleAdsCalloutsProps {
  customerId: string;
}

/** Example ad copy around the callouts row — there's no real ad on an account settings page,
 * so only the callouts themselves are ever real. */
interface IAdPreviewProps {
  calloutText: string;
}

function AdPreview({ calloutText }: Readonly<IAdPreviewProps>): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={styles.preview}>
      <Text.DefaultRegular className={styles.previewHeadline}>
        {t('Support Your Cause | Get Involved Today')}
      </Text.DefaultRegular>
      <div className={styles.previewUrlRow}>
        <span className={styles.previewAdBadge}>{t('Ad')}</span>
        <Text.DefaultRegular>{t('www.example.org/donate')}</Text.DefaultRegular>
      </div>
      <Text.DefaultRegular>
        {t('A short description of your organization and what donors help fund.')}
      </Text.DefaultRegular>
      {!!calloutText && (
        <div className={styles.previewCallouts} data-name={'google_ads_callouts_preview'}>
          <Text.DefaultRegular>{calloutText}</Text.DefaultRegular>
        </div>
      )}
      <div className={styles.previewSitelinks}>
        <Text.DefaultRegular className={styles.previewSitelink}>
          {t('About us')}
        </Text.DefaultRegular>
        <Text.DefaultRegular className={styles.previewSitelink}>
          {t('Our programs')}
        </Text.DefaultRegular>
        <Text.DefaultRegular className={styles.previewSitelink}>
          {t('Donate now')}
        </Text.DefaultRegular>
      </div>
    </div>
  );
}

/**
 * Account-level Google Ad Grants callout assets, shown alongside a connected customer.
 */
function GoogleAdsCallouts({ customerId }: Readonly<IGoogleAdsCalloutsProps>): JSX.Element {
  const { t } = useTranslation();
  const { Campaigns, GoogleAdsCallouts: Callouts } = useStore();
  // Callouts only ever apply to this account's Google Ads Search/Smart campaigns — every
  // other campaign type (email, display, social, ...) is irrelevant here.
  const campaignCount = Campaigns.count({
    filters: { _cls__in: [CampaignClass.GoogleAdsSearch, CampaignClass.GoogleAdsSmart] },
  });
  const response = Callouts.list({ customer: customerId });
  const saved = response.models;

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [drafts, setDrafts] = useState<IDraftCallout[]>([]);
  const [removingKey, setRemovingKey] = useState<string | undefined>(undefined);

  const removingDraft = drafts.find((draft) => draft.key === removingKey);
  /*
   * Duplicate checking is symmetric (any two matching rows both "conflict"), but showing the
   * message under both would leave two identical error strings on screen at once. Comparing
   * each row only against the ones before it keeps a single message per collision, on the
   * later row.
   */
  const errorsByKey = new Map(
    drafts.map((draft, index) => [
      draft.key,
      getDraftCalloutError(draft, drafts.slice(0, index + 1), t),
    ]),
  );
  const errorMessages = [...errorsByKey.values()].filter((error): error is string => !!error);
  const hasErrors = errorMessages.length > 0;
  const canSave = isDraftDirty(drafts, saved) && !hasErrors;
  const doneTooltip = hasErrors ? errorMessages[0] : undefined;

  function handleEdit(): void {
    setDrafts(saved.map(createDraftCallout));
    setIsEditing(true);
  }

  function handleCancel(): void {
    setDrafts([]);
    setIsEditing(false);
  }

  function handleAdd(): void {
    setDrafts([...drafts, createEmptyDraftCallout()]);
  }

  function handleChange(key: string): (value?: string) => void {
    return function (value?: string): void {
      // No length clamp here — text over the limit has to stay on screen so the inline
      // error can actually show, rather than silently truncating what the user typed.
      setDrafts(
        drafts.map((draft) => (draft.key === key ? { ...draft, text: value ?? '' } : draft)),
      );
    };
  }

  function handleRemove(draft: IDraftCallout): () => void {
    return function (): void {
      setRemovingKey(draft.key);
    };
  }

  function handleCancelRemove(): void {
    setRemovingKey(undefined);
  }

  function handleConfirmRemove(): void {
    if (!removingDraft) {
      return;
    }
    setDrafts(drafts.filter((draft) => draft.key !== removingDraft.key));
    setRemovingKey(undefined);
    toast(
      t('Removed "{{- text}}". Save to apply the change to your account.', {
        text: removingDraft.text,
      }),
      { type: 'info' },
    );
  }

  async function handleDone(): Promise<void> {
    const { toCreate, toEdit, toDeleteIds } = diffDraftCallouts(drafts, saved);
    setIsSaving(true);
    try {
      const createResults = await Promise.all(
        toCreate.map(async (draft) => {
          const model = Callouts.create({ customer: customerId, callout_text: draft.text.trim() });
          // `refreshApiCache` would silently re-fetch every cached list in the background and
          // overwrite the edits/removals this same "Done" click is also making — see
          // GoogleAdsAdGroups' identical `add` call for the same reason.
          const created = await Callouts.add(model, { refreshApiCache: false });
          if (!created.isErrored) {
            /*
             * `add` only inserts the server-assigned model into `modelsById`, not into this
             * list's `apiCache` entry (`saved` reads from the latter) — and `refreshApiCache:
             * false` above means nothing else will either. Mirror GoogleAdsAdGroups'
             * `addAdGroup`: drop the now-stale locally-keyed model and push the real one in.
             */
            Callouts.removeEphemeral(model.id);
            runInAction(() => {
              response.models.push(created);
            });
          }
          return { draft, model: created };
        }),
      );
      const editResults = await Promise.all(
        toEdit.map(async (edit) => ({
          edit,
          model: await Callouts.save(edit.id, { callout_text: edit.text }),
        })),
      );
      const deleteResults = await Promise.all(
        toDeleteIds.map(async (id) => {
          const model = saved.find((callout) => callout.id === id);
          if (!model) {
            return undefined;
          }
          await model.delete();
          return model;
        }),
      );

      /*
       * Stamp the real id onto every draft whose create just succeeded. Without this, a
       * create that succeeded in the same batch as a failure would still look uncreated on
       * the next "Done" click, so a retry would re-create it — a genuine duplicate once the
       * user has since edited its text (the 409 duplicate check only catches unchanged text).
       */
      const createdIdsByKey = new Map(
        createResults
          .filter((result) => !result.model.isErrored)
          .map((result) => [result.draft.key, result.model.id]),
      );
      if (createdIdsByKey.size > 0) {
        setDrafts((current) =>
          current.map((draft) =>
            createdIdsByKey.has(draft.key)
              ? { ...draft, id: createdIdsByKey.get(draft.key) }
              : draft,
          ),
        );
      }

      const failed = [
        ...createResults.map((result) => result.model),
        ...editResults.map((result) => result.model),
        ...deleteResults,
      ].filter((model): model is GoogleAdsCallout => !!model?.isErrored);

      if (failed.length > 0) {
        toast(t("Couldn't save {{count}} callout(s). Try again.", { count: failed.length }), {
          type: 'error',
        });
        return;
      }

      toast(
        t(
          'Saved {{count}} callouts. They apply to all {{campaignCount}} Google campaigns on this account.',
          { count: saved.length, campaignCount },
        ),
        { type: 'success' },
      );
      setDrafts([]);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }

  const previewSource = isEditing ? drafts : saved.map(createDraftCallout);
  const previewCalloutText = previewSource
    .map((draft) => draft.text.trim())
    .filter(Boolean)
    .join(' · ');
  const count = isEditing ? drafts.length : saved.length;

  return (
    <Card.Content contentClassName={styles.root} name={'google_ads_callouts'} padding={'none'}>
      <div className={styles.header}>
        <div>
          <Heading order={2} variant={'h5'}>
            {t('Callouts')}
          </Heading>
          {!response.isPending && !response.isErrored && (
            <Text.DefaultRegular truncate={false}>
              {t('{{count}} callouts · how your ads will look', { count })}
            </Text.DefaultRegular>
          )}
        </div>
        {!response.isPending && !response.isErrored && !isEditing && (
          <Button onClick={handleEdit} type={'secondary'}>
            {t('Edit')}
          </Button>
        )}
        {isEditing && (
          <div className={styles.actions}>
            <Button disabled={isSaving} onClick={handleCancel} type={'secondary'}>
              {t('Cancel')}
            </Button>
            <Button
              disabled={!canSave || isSaving}
              onClick={handleDone}
              tooltip={doneTooltip}
              type={'primary'}
            >
              {t('Done')}
            </Button>
          </div>
        )}
      </div>
      {response.isPending && <Spinner />}
      {!response.isPending && response.isErrored && (
        <Alert
          description={t("We couldn't load your callouts. Try reloading the page.")}
          title={t("Couldn't load callouts.")}
          type={'danger'}
        />
      )}
      {!response.isPending && !response.isErrored && saved.length === 0 && !isEditing && (
        <Well>
          <Text.DefaultRegular truncate={false}>
            {t(
              'No callouts yet. Add one to tell people more about your organization wherever your ads appear.',
            )}
          </Text.DefaultRegular>
        </Well>
      )}
      {!response.isPending && !response.isErrored && (saved.length > 0 || isEditing) && (
        <AdPreview calloutText={previewCalloutText} />
      )}
      {!response.isPending && !response.isErrored && (
        <Text.DefaultRegular className={styles.previewNote} truncate={false}>
          {t(
            'Google shows up to 10 callouts on an ad and chooses which ones to display, so this is an example rather than a fixed set.',
          )}
        </Text.DefaultRegular>
      )}
      {!response.isPending && !response.isErrored && isEditing && (
        <>
          <div className={styles.rows}>
            {drafts.map((draft, index) => {
              const error = errorsByKey.get(draft.key);
              const showError = !!error && (draft.text.trim() !== '' || !!draft.id);
              return (
                <div className={styles.row} key={draft.key}>
                  <Text.DefaultRegular className={styles.rowLabel}>
                    {t('Callout {{n}}', { n: index + 1 })}
                  </Text.DefaultRegular>
                  <div className={styles.rowInput}>
                    <div className={styles.rowInputColumn}>
                      <Input
                        aria-label={t('Callout {{n}}', { n: index + 1 })}
                        onChange={handleChange(draft.key)}
                        validationError={showError ? error : undefined}
                        value={draft.text}
                      />
                      <div className={styles.rowCounter}>
                        <Counter maxLength={MAX_CALLOUT_TEXT} value={draft.text} />
                      </div>
                    </div>
                    <Button
                      aria-label={t('Remove {{- text}}', { text: draft.text })}
                      onClick={handleRemove(draft)}
                      tooltip={t('Remove')}
                      type={'icon'}
                    >
                      {getIconForAction('delete')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <Button
            name={'add_callout'}
            onClick={handleAdd}
            prefix={getIconForAction('add')}
            type={'secondary'}
          >
            {t('Add callout')}
          </Button>
        </>
      )}
      {!!removingDraft && (
        <ConfirmModal
          confirmButtonText={t('Remove')}
          confirmButtonType={'danger'}
          description={t(
            'Callouts are stored on your Google Ads account, so removing "{{- text}}" removes it from all {{count}} Google campaigns on this account.',
            { text: removingDraft.text, count: campaignCount },
          )}
          onClose={handleCancelRemove}
          onConfirm={handleConfirmRemove}
          opened={true}
          t={t}
          title={t('Remove callout?')}
        />
      )}
    </Card.Content>
  );
}

export default observer(GoogleAdsCallouts);
